import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { MockBadge, Input, Spinner } from "../components/ui";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Hammer, ArrowLeft, AlertTriangle, Check, X, Wrench, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const fmtEur = (n) => `€${Math.round(n).toLocaleString()}`;
const fmtPct = (n) => `${n.toFixed(1).replace(".", ",")}%`;

// Engine alert key → i18n key + style
const ALERT_MAP = {
  deal_not_computable: { key: "fixflip.alertNotComputable", style: "danger" },
  mq_critical: { key: "fixflip.alertMqCritical", style: "danger" },
  mq_tight: { key: "fixflip.alertMqTight", style: "warn" },
  mq_solid: { key: "fixflip.alertMqSolid", style: "success" },
  puffer_low: { key: "fixflip.alertPufferLow", style: "warn" },
};

const VERDICT_STYLE = {
  buy: { cls: "text-signal-green border-signal-green/50 bg-signal-green/10", Icon: Check },
  negotiate: { cls: "text-signal-yellow border-signal-yellow/50 bg-signal-yellow/10", Icon: AlertTriangle },
  reject: { cls: "text-signal-red border-signal-red/50 bg-signal-red/10", Icon: X },
  none: { cls: "text-text-muted border-border bg-surface", Icon: Hammer },
};

// Sanierungskosten-Assistent levels (€/m²) — real benchmarks from the webinar
// deals (CONCEPT-FF-RECHNER.md §8.1.3): 30k @ 40–47 m² standard, 60k @ 30 m² Altbau.
const SAN_LEVELS = [
  { key: "refresh", rate: 550 },
  { key: "voll", rate: 1000 },
  { key: "altbau", rate: 2000 },
];

export default function FixFlip() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const id = params.get("id");
  const nav = useNavigate();
  const [form, setForm] = useState({
    vk: 250000,
    san: 30000,
    marge: 45000,
    puffer_pct: 15,
    nk_pct: 10,
    vkosten_pct: 3,
    haltekosten_monthly: 0,
    dauer_monate: 9,
  });
  const [angebot, setAngebot] = useState(0);
  const [miete, setMiete] = useState(0);
  const [sizeM2, setSizeM2] = useState(0);
  const [sanLevel, setSanLevel] = useState("voll");
  const [prefill, setPrefill] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Prefill from listing + market data (CONCEPT-FF-RECHNER.md §5.1/§5.2):
  // asking price → Deal-Check, size → San-Assistent, rent → Plan-B,
  // market → real Nebenkosten + median-based VK estimate.
  useEffect(() => {
    if (!id) return;
    api.get(`/listings/${id}`).then(async ({ data }) => {
      const l = data.listing;
      if (l?.price) setAngebot(l.price);
      if (l?.size_m2) setSizeM2(l.size_m2);
      if (l?.annual_net_rent) setMiete(Math.round(l.annual_net_rent / 12));
      const market = l?.market_code || l?.market;
      if (market) {
        try {
          const { data: pf } = await api.get("/fixflip/prefill", {
            params: { market, size_m2: l?.size_m2 || undefined },
          });
          setPrefill(pf);
          if (pf.nk_pct != null) setForm((f) => ({ ...f, nk_pct: pf.nk_pct }));
          if (!l?.annual_net_rent && pf.rent_est) setMiete(pf.rent_est);
        } catch { /* prefill is a convenience — the calculator works without it */ }
      }
    }).catch(() => {});
  }, [id]);

  // Debounced live recalc — the benchmark's realtime feel, engine stays authoritative.
  useEffect(() => {
    const h = setTimeout(async () => {
      try {
        const { data } = await api.post("/fixflip", {
          ...form,
          angebot: angebot || null,
          miete: miete || null,
        });
        setResult(data);
        setError(null);
      } catch (e) {
        setError(e?.response?.data?.detail || "F&F-Rechner failed — check inputs.");
      }
    }, 250);
    return () => clearTimeout(h);
  }, [form, angebot, miete]);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const dc = result?.deal_check;
  const pb = result?.plan_b;
  const verdict = VERDICT_STYLE[dc?.verdict ?? "none"];
  const sanRate = SAN_LEVELS.find((s) => s.key === sanLevel)?.rate ?? 1000;

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          {id && (
            <button onClick={() => nav(`/listings/${id}`)} className="overline hover:text-white inline-flex items-center gap-2 mb-3">
              <ArrowLeft size={12} /> {t("fixflip.backToListing")}
            </button>
          )}
          <div className="overline">{t("fixflip.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("fixflip.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">{t("fixflip.subtitle")}</p>
        </div>
        <MockBadge />
      </div>

      <div className="grid grid-cols-12">
        {/* INPUTS */}
        <section className="col-span-12 lg:col-span-5 border-r border-border p-6 space-y-5">
          <div className="overline">{t("fixflip.inputs")}</div>
          <Field label={t("fixflip.vkLabel")} hint={t("fixflip.vkHint")}>
            <Input data-testid="ff-vk" type="number" value={form.vk} onChange={(e) => set("vk", parseFloat(e.target.value) || 0)} />
            {prefill?.vk_est && (
              <button
                onClick={() => set("vk", prefill.vk_est)}
                className="mt-1 text-[10px] font-mono text-signal-green hover:underline"
                data-testid="ff-vk-prefill"
              >
                {t("fixflip.vkPrefill", {
                  est: fmtEur(prefill.vk_est),
                  med: prefill.med_price_sqm?.toLocaleString(),
                  market: prefill.market_name,
                  uplift: prefill.vk_uplift,
                })}
              </button>
            )}
          </Field>
          <Field label={t("fixflip.sanLabel")} hint={t("fixflip.sanHint")}>
            <Input data-testid="ff-san" type="number" value={form.san} onChange={(e) => set("san", parseFloat(e.target.value) || 0)} />
            {form.san === 0 && (
              <div className="mt-1 text-[10px] font-mono text-signal-green">{t("fixflip.buyFlipNote")}</div>
            )}
            {/* Sanierungskosten-Assistent */}
            <div className="mt-2 border border-border/60 p-2 flex items-end gap-2">
              <div className="w-20">
                <div className="overline mb-1">{t("fixflip.sanAssistM2")}</div>
                <Input type="number" value={sizeM2 || ""} onChange={(e) => setSizeM2(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="flex-1">
                <div className="overline mb-1">{t("fixflip.sanAssistLevel")}</div>
                <select
                  value={sanLevel}
                  onChange={(e) => setSanLevel(e.target.value)}
                  className="w-full bg-surface border border-border text-white font-mono text-xs px-2 py-2 focus:border-signal-green outline-none"
                >
                  {SAN_LEVELS.map((s) => (
                    <option key={s.key} value={s.key}>{t(`fixflip.sanLevel.${s.key}`)}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => sizeM2 > 0 && set("san", sizeM2 * sanRate)}
                disabled={!sizeM2}
                className="inline-flex items-center gap-1.5 border border-signal-green/40 text-signal-green hover:bg-signal-green hover:text-black font-mono text-[10px] uppercase tracking-widest2 px-2.5 py-2 transition-colors disabled:opacity-40"
                data-testid="ff-san-apply"
              >
                <Wrench size={11} /> {sizeM2 ? fmtEur(sizeM2 * sanRate) : "—"}
              </button>
            </div>
          </Field>
          <Field label={t("fixflip.margeLabel")} hint={t("fixflip.margeHint")}>
            <Input data-testid="ff-marge" type="number" value={form.marge} onChange={(e) => set("marge", parseFloat(e.target.value) || 0)} />
          </Field>

          <Slider
            label={t("fixflip.pufferLabel")} value={form.puffer_pct} display={`${form.puffer_pct}%`}
            min={0} max={50} step={5} testid="ff-puffer"
            onChange={(v) => set("puffer_pct", v)}
            hints={[t("fixflip.pufferHintRisky"), t("fixflip.pufferHintRec"), t("fixflip.pufferHintAltbau")]}
            footnote={t("fixflip.pufferSizeNote")}
          />
          <Slider
            label={t("fixflip.nkLabel")} value={form.nk_pct} display={fmtPct(form.nk_pct)}
            min={0} max={15} step={0.5} testid="ff-nk"
            onChange={(v) => set("nk_pct", v)}
            hints={[prefill ? t("fixflip.nkHintPrefill", { pct: fmtPct(prefill.nk_pct), market: prefill.market_name }) : t("fixflip.nkHint")]}
          />
          <Slider
            label={t("fixflip.vkostenLabel")} value={form.vkosten_pct} display={fmtPct(form.vkosten_pct)}
            min={0} max={6} step={0.5} testid="ff-vkosten"
            onChange={(v) => set("vkosten_pct", v)}
            hints={[t("fixflip.vkostenHint0"), t("fixflip.vkostenHintUsual"), t("fixflip.vkostenHintMakler")]}
          />

          {/* Erweitert: Haltekosten (opt-in — the benchmark omits them deliberately) */}
          <details className="border border-border/60 group">
            <summary className="overline cursor-pointer hover:text-white px-3 py-2">{t("fixflip.advancedTitle")}</summary>
            <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-3">
              <Field label={t("fixflip.haltekostenLabel")}>
                <Input data-testid="ff-haltekosten" type="number" value={form.haltekosten_monthly} onChange={(e) => set("haltekosten_monthly", parseFloat(e.target.value) || 0)} />
              </Field>
              <Field label={t("fixflip.dauerLabel")}>
                <Input data-testid="ff-dauer" type="number" value={form.dauer_monate} onChange={(e) => set("dauer_monate", parseFloat(e.target.value) || 9)} />
              </Field>
              <p className="col-span-2 text-[10px] font-mono text-text-muted">{t("fixflip.haltekostenHint")}</p>
            </div>
          </details>
        </section>

        {/* RESULTS */}
        <section className="col-span-12 lg:col-span-7 p-6">
          <div className="overline">{t("fixflip.output")}</div>
          {error ? (
            <div className="mt-6 border border-signal-red/40 bg-signal-red/10 p-4 text-xs font-mono text-signal-red">{error}</div>
          ) : !result ? (
            <div className="mt-6"><Spinner /></div>
          ) : (
            <div className="mt-3 space-y-6">
              {/* Hero: Maximaler Ankaufspreis */}
              <div className="border border-border p-5 ai-glow">
                <div className="overline">{t("fixflip.maxLabel")}</div>
                <div className={`num text-4xl mt-2 font-bold ${result.valid ? "text-signal-green" : "text-signal-red"}`}>
                  {result.valid ? fmtEur(result.max_ankauf) : "—"}
                </div>
                <p className="mt-2 text-[11px] text-text-muted">{t("fixflip.maxCaption")}</p>
              </div>

              {/* Metric tiles */}
              <div className="grid grid-cols-3 gap-px bg-border border border-border">
                <KPI label={t("fixflip.tileNk")} value={result.valid ? fmtEur(result.nebenkosten_eur) : "—"} />
                <KPI label={t("fixflip.tileSan")} value={fmtEur(result.san_total)} />
                <KPI
                  label={t("fixflip.tileGewinn")}
                  value={fmtEur(result.marge)}
                  accent={{ solide: "green", knapp: "yellow", kritisch: "red" }[result.mq_ampel]}
                />
              </div>
              <p className="-mt-4 text-right text-[10px] font-mono text-text-muted">
                {t("fixflip.margeMonthly", {
                  monthly: fmtEur(form.dauer_monate > 0 ? result.marge / form.dauer_monate : 0),
                  months: form.dauer_monate,
                })}
              </p>

              {/* Margen-Quote bar */}
              <div>
                <div className="flex items-center justify-between">
                  <span className="overline">{t("fixflip.mqLabel")}</span>
                  <span className="num text-sm text-white">{form.vk > 0 ? `${Math.round(result.margen_quote_pct)}%` : "—"}</span>
                </div>
                <div className="mt-2 h-2 bg-surface border border-border">
                  <div
                    className={`h-full transition-all ${{ solide: "bg-signal-green", knapp: "bg-signal-yellow", kritisch: "bg-signal-red" }[result.mq_ampel]}`}
                    style={{ width: `${Math.min(result.margen_quote_pct * 2.5, 100)}%` }}
                  />
                </div>
                <div className="mt-1 flex gap-3 text-[10px] font-mono text-text-muted">
                  <span>{t("fixflip.mqLegendCritical")}</span>
                  <span>{t("fixflip.mqLegendTight")}</span>
                  <span>{t("fixflip.mqLegendSolid")}</span>
                </div>
              </div>

              {/* Breakdown receipt */}
              <div className="border border-border">
                <div className="px-4 py-2 border-b border-border bg-surface">
                  <span className="overline text-text-muted">{t("fixflip.breakdownTitle")}</span>
                </div>
                <Row label={t("fixflip.bVk")} value={fmtEur(form.vk)} />
                <Row label={t("fixflip.bVkosten")} value={`− ${fmtEur(result.verkaufskosten_eur)}`} negative />
                <Row label={t("fixflip.bMarge")} value={`− ${fmtEur(result.marge)}`} negative />
                <Row label={t("fixflip.bSan")} value={`− ${fmtEur(result.san_total)}`} negative />
                {result.haltekosten_total > 0 && (
                  <Row
                    label={t("fixflip.bHaltekosten", { monthly: fmtEur(form.haltekosten_monthly), months: form.dauer_monate })}
                    value={`− ${fmtEur(result.haltekosten_total)}`} negative
                  />
                )}
                <Row label={t("fixflip.bNk")} value={result.valid ? `− ${fmtEur(result.nebenkosten_eur)}` : "—"} negative />
                <Row label={t("fixflip.bMax")} value={result.valid ? fmtEur(result.max_ankauf) : "—"} bold accent={result.valid ? "green" : "red"} />
              </div>

              {/* Alerts */}
              <div className="space-y-2">
                {result.alerts.map((a) => {
                  const m = ALERT_MAP[a];
                  if (!m) return null;
                  const cls = {
                    danger: "border-signal-red/40 bg-signal-red/5 text-signal-red",
                    warn: "border-signal-yellow/40 bg-signal-yellow/5 text-signal-yellow",
                    success: "border-signal-green/40 bg-signal-green/5 text-signal-green",
                  }[m.style];
                  const Icon = m.style === "success" ? Check : AlertTriangle;
                  return (
                    <div key={a} className={`border p-3 flex gap-3 ${cls}`}>
                      <Icon size={13} className="shrink-0 mt-0.5" />
                      <p className="text-xs leading-relaxed text-text-secondary">{t(m.key)}</p>
                    </div>
                  );
                })}
              </div>

              {/* Plan-B-Ampel */}
              <div className="border border-border">
                <div className="px-4 py-2 border-b border-border bg-surface flex items-center gap-2">
                  <ShieldCheck size={12} className="text-text-muted" />
                  <span className="overline text-text-muted">{t("fixflip.planBTitle")}</span>
                </div>
                <div className="p-4">
                  <div className="flex items-end gap-3 flex-wrap">
                    <div className="w-40">
                      <div className="overline mb-1">{t("fixflip.planBRentLabel")}</div>
                      <Input data-testid="ff-miete" type="number" value={miete || ""} onChange={(e) => setMiete(parseFloat(e.target.value) || 0)} />
                    </div>
                    {pb && pb.ampel !== "none" && (
                      <div className={`px-3 py-2 border font-mono text-xs uppercase tracking-widest2 ${
                        { green: "text-signal-green border-signal-green/50 bg-signal-green/10",
                          yellow: "text-signal-yellow border-signal-yellow/50 bg-signal-yellow/10",
                          red: "text-signal-red border-signal-red/50 bg-signal-red/10" }[pb.ampel]
                      }`} data-testid="ff-planb-ampel">
                        {t(`fixflip.planBAmpel.${pb.ampel}`, { faktor: pb.faktor })}
                      </div>
                    )}
                    <button
                      onClick={() => nav("/objekt-cockpit")}
                      className="overline hover:text-white inline-flex items-center gap-1.5 pb-2"
                    >
                      {t("fixflip.planBUnderwrite")} →
                    </button>
                  </div>
                  <p className="mt-2 text-[10px] font-mono text-text-muted">{t("fixflip.planBHint")}</p>
                </div>
              </div>

              {/* Doctrine + disclaimer */}
              <p className="text-[11px] text-text-muted leading-relaxed border-t border-border pt-4">
                {t("fixflip.profiRule")}
                <br />
                <span className="text-zinc-600">{t("fixflip.taxNote")}</span>
              </p>
            </div>
          )}
        </section>
      </div>

      {/* DEAL-CHECK */}
      <section className="border-t border-border p-6 md:px-10">
        <div className="overline">{t("fixflip.dcTitle")}</div>
        <p className="mt-1 text-xs text-text-muted">{t("fixflip.dcSubtitle")}</p>
        <div className="mt-4 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <Field label={t("fixflip.angebotLabel")}>
              <Input data-testid="ff-angebot" type="number" value={angebot || ""} onChange={(e) => setAngebot(parseFloat(e.target.value) || 0)} />
            </Field>
            <div className="mt-4 border border-border">
              <Row label={t("fixflip.dcAngebot")} value={angebot > 0 ? fmtEur(angebot) : "—"} />
              <Row label={t("fixflip.dcMax")} value={result?.valid ? fmtEur(result.max_ankauf) : "—"} />
              <Row
                label={t("fixflip.dcDiff")}
                value={dc && dc.verdict !== "none" ? `${dc.diff > 0 ? "+ " : "− "}${fmtEur(Math.abs(dc.diff))}` : "—"}
                bold
              />
            </div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className={`border p-5 flex items-start gap-4 ${verdict.cls}`}>
              <verdict.Icon size={22} className="shrink-0 mt-1" />
              <div>
                <div className="font-heading text-2xl font-black tracking-tight" data-testid="ff-verdict">
                  {dc && dc.verdict !== "none"
                    ? t(`fixflip.verdict${dc.verdict === "buy" ? "Buy" : dc.verdict === "negotiate" ? "Negotiate" : "Reject"}`)
                    : "—"}
                </div>
                <p className="mt-2 text-xs text-text-secondary leading-relaxed">
                  {!dc || dc.verdict === "none"
                    ? (!result?.valid ? t("fixflip.dcNeedCalc") : t("fixflip.dcNeedAngebot"))
                    : dc.verdict === "buy"
                      ? t("fixflip.dcDetailBuy", { diff: fmtEur(Math.abs(dc.diff)) })
                      : dc.verdict === "negotiate"
                        ? t("fixflip.dcDetailNegotiate", { diff: fmtEur(dc.diff), pct: fmtPct(dc.diff_pct) })
                        : t("fixflip.dcDetailReject", { pct: fmtPct(dc.diff_pct) })}
                </p>
                <p className="mt-3 text-[10px] font-mono uppercase tracking-widest2 text-text-muted">
                  {t("fixflip.merksatz")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      {children}
      {hint && <div className="mt-1 text-[10px] font-mono text-text-muted">{hint}</div>}
    </div>
  );
}

function Slider({ label, value, display, min, max, step, onChange, hints = [], footnote, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="overline">{label}</span>
        <span className="num text-sm text-white">{display}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        data-testid={testid}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-signal-green"
      />
      {hints.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono text-text-muted">
          {hints.map((h, i) => <span key={i}>{h}</span>)}
        </div>
      )}
      {footnote && <div className="mt-1 text-[10px] font-mono text-zinc-600">{footnote}</div>}
    </div>
  );
}

function KPI({ label, value, accent = "white" }) {
  const cls = { white: "text-white", green: "text-signal-green", yellow: "text-signal-yellow", red: "text-signal-red" }[accent];
  return (
    <div className="bg-bg p-4">
      <div className="overline">{label}</div>
      <div className={`num text-xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, negative, bold, accent }) {
  const accentCls = accent === "green" ? "text-signal-green" : accent === "red" ? "text-signal-red" : negative ? "text-text-secondary" : "text-white";
  return (
    <div className={`flex items-center justify-between px-4 py-3 border-b border-border last:border-b-0 ${bold ? "bg-surface" : ""}`}>
      <span className={`text-xs ${bold ? "font-mono uppercase tracking-widest2 text-text-muted" : "text-text-secondary"}`}>{label}</span>
      <span className={`num ${bold ? "text-lg" : "text-sm"} ${accentCls}`}>{value}</span>
    </div>
  );
}
